import {
	fetchFicheProduit,
	fetchFicheProduitUri,
	fetchFirstProducts,
} from "app/pages/Listing/productActionCreators";
import {
	fetchMerchandisingLight,
	fetchMerchandisings,
} from "app/pages/Merchandising/merchandisingActionCreators";
import { getContextualizedSignupPagesPagesAuth } from "app/pages/SignupPage/Auth/SignupAuthActionCreators";
import {
	fetchSDPAccommodationDescription,
	fetchSDPFilters,
} from "app/pages/SmartDP/smartDPActionCreators";
import { fetchLandingPage } from "app/pages/LandingPage/landingPageActionCreators";
import { fetchPaymentMethods } from "app/pages/Booking/Payment/paymentActionCreators";
import { fetchHotelOnlyAccommodationDescription } from "app/pages/HotelOnly/Booking/pages/Quote/BookingQuoteActionCreator";
import { fetchHotelOnlySearchFormAvailability } from "app/pages/HotelOnly/hotelOnlyActionCreator";
import { fetchDefaultTunnelSearchFilters } from "app/pages/.shared/WebsiteTunnelSwitcher/tunnelSearchFiltersActionCreators";
import { getHotelAccommodationDescriptionQuery } from "app/features/hotel/hotelDescription/hooks/useHotelAccommodationDescription";
import { parseTunnelParam } from "app/types/tunnel";
import { getHeroCommercialMerchandisingQuery } from "app/features/heroCommercial";
import { getActiveHeroCommercialMerchCode } from "app/pages/Merchandising/merchandisingSelector";

// Lu après fetchMerchandisings : le code du hero vient de la liste des merchs chargée par la route.
const getHomeHeroCommercialQuery = ({ state, shop, productsVisibility }) => {
	const heroCommercialCode = getActiveHeroCommercialMerchCode(state);

	if (!heroCommercialCode) {
		return null;
	}

	return getHeroCommercialMerchandisingQuery({
		code: heroCommercialCode,
		shop,
		productsVisibility,
		partnerCode: state.partner?.code,
	});
};

// Exception à la règle « pas de nouveaux ssrActionCreators » : Booking.jsx protège /booking/quote
// selon les merchandisings de la fiche produit (Redux). Sans elle au SSR, la protection bascule
// après son fetch client et remonte QuotationContainer, ce qui annule la première quote.
const fetchQuotationFicheProduit = ({ query }) =>
	query?.productUri ? fetchFicheProduit({ productUri: query.productUri }) : undefined;

// Exception à la règle « pas de nouveaux ssrActionCreators » : les filtres des formulaires de
// recherche restent en Redux, et les charger ici évite que le champ cabine apparaisse ou
// disparaisse après l'hydratation. Toujours en fin de liste : loadDataResponses[0] est lu.
export const getRoutes = shop => [
	{
		path: `/${shop}/home`,
	},
	{
		path: `/${shop}/home/login`,
	},
	{
		path: `/${shop}/home/signup`,
	},
	{
		path: `/${shop}/auth`,
	},
	{
		path: `/${shop}/giveaway`,
	},
	{
		path: `/${shop}/unsubscribe`,
	},
	{
		path: `/${shop}/account`,
	},
	{
		path: `/${shop}/account/profile`,
	},
	{
		path: `/${shop}/account/customer-request/:request`,
	},
	{
		path: `/${shop}/account/mybookings/:bookingReference`,
	},
	{
		path: `/${shop}/account/mybookings`,
	},
	{
		path: `/${shop}/account/security`,
	},
	{
		path: `/${shop}/account/subscriptions`,
	},
	{
		path: `/${shop}/account/sponsorship`,
	},
	{
		path: `/${shop}/account/mycredits`,
	},
	{
		path: `/${shop}/account/myrefunds`,
	},
	{
		path: `/${shop}/account/faq-contact`,
	},
	{
		path: `/${shop}/account/faq-chatbot`,
	},
	{
		path: `/${shop}/sign-up/:operationCode/*`,
		ssrActionCreators: [getContextualizedSignupPagesPagesAuth],
	},
	{
		path: `/${shop}/landing-pages/:landingPageCode`,
		ssrActionCreators: [fetchLandingPage],
	},
	{
		path: `/${shop}/schizophrene`,
	},
	{
		path: `/${shop}/priceMatchGuarantee`,
	},
	{
		path: `/${shop}/booking`,
	},
	{
		path: `/${shop}/external-redirect`,
	},
	{
		path: `/${shop}/confirmation`,
	},
	{
		path: `/${shop}/not-found`,
	},
	{
		path: `/${shop}/technical-error`,
	},
	{
		path: `/${shop}/technical-error/registration-confirmation-expired`,
	},
	{
		path: `/${shop}/technical-error/confirm-registration-failed`,
	},
	{
		path: `/${shop}/technical-error/sso`,
	},
	{
		path: `/${shop}/technical-error/duplicate-booking`,
	},
	{
		path: `/${shop}/mybookings`,
	},
	{
		path: `/${shop}/magazine`,
	},
	{
		path: `/${shop}/listing`,
		ssrActionCreators: [fetchFirstProducts, fetchMerchandisings, fetchPaymentMethods],
	},
	{
		path: `/${shop}/merch/:merchCode`,
		ssrActionCreators: [fetchMerchandisingLight, fetchPaymentMethods],
	},
	{
		path: `/${shop}/stopover/search`,
		ssrActionCreators: [fetchSDPFilters],
	},
	{
		path: `/${shop}/stopover/listing`,
		ssrActionCreators: [fetchSDPFilters],
	},
	{
		path: `/${shop}/stopover/booking/quote`,
	},
	{
		path: `/${shop}/stopover/booking/payment`,
	},
	{
		path: `/${shop}/stopover/booking/confirmation`,
	},
	{
		path: `/${shop}/stopover/hotelDescription/:accommodationId/:code`,
		ssrActionCreators: [fetchSDPAccommodationDescription],
	},
	{
		path: `/${shop}/sdp/search`,
		ssrActionCreators: [fetchSDPFilters],
	},
	{
		path: `/${shop}/sdp/listing`,
		ssrActionCreators: [fetchSDPFilters],
	},
	{
		path: `/${shop}/sdp/listing/map`,
		ssrActionCreators: [fetchSDPFilters],
	},
	{
		path: `/${shop}/sdp/booking/quote`,
		ssrQueries: [
			({ query, partnerCode }) =>
				getHotelAccommodationDescriptionQuery(query?.accommodationId, "sdp", partnerCode),
		],
	},
	{
		path: `/${shop}/sdp/booking/payment`,
	},
	{
		path: `/${shop}/sdp/booking/confirmation`,
	},
	{
		path: `/${shop}/sdp/hotelDescription/:accommodationId/:code`,
		ssrActionCreators: [fetchSDPAccommodationDescription],
	},
	{
		path: `/${shop}/hotel/:tunnel/:country/:resort/:propertyType/:accommodationId/:hotelName`,
		ssrQueries: [
			({ tunnel, accommodationId, partnerCode }) =>
				getHotelAccommodationDescriptionQuery(
					accommodationId,
					parseTunnelParam(tunnel),
					partnerCode
				),
		],
	},
	{
		path: `/${shop}/hotelonly/hotelDescription/:accommodationId/:code`,
		ssrActionCreators: [fetchHotelOnlyAccommodationDescription],
	},
	{
		path: `/${shop}/hotelonly/search`,
		ssrActionCreators: [fetchHotelOnlySearchFormAvailability],
	},
	{
		path: `/${shop}/hotelonly/listing/*`,
		ssrActionCreators: [fetchHotelOnlySearchFormAvailability],
	},
	{
		path: `/${shop}/product/id/:productId`,
		ssrActionCreators: [fetchFicheProduitUri],
	},
	{
		path: `/${shop}/booking/quote`,
		ssrActionCreators: [fetchQuotationFicheProduit],
	},
	{
		path: `/${shop}/booking/payment`,
	},
	{
		path: `/${shop}/booking/confirmation`,
	},
	{
		path: `/${shop}/booking-auth`,
	},
	{
		path: `/${shop}/booking-search`,
	},
	{
		path: `/${shop}/ourcommitments`,
	},
	{
		path: `/${shop}/faq`,
	},
	{
		path: `/${shop}/faq/category/:categoryId`,
	},
	{
		path: `/${shop}/faq/category/:categoryId/subcategory/:subCategoryId`,
	},
	{
		path: `/${shop}/faq/category/:categoryId/subcategory/:subCategoryId/article/:articleId`,
	},
	{
		path: `/${shop}/page/:slug`,
	},
	{
		path: `/${shop}/cookie-policy`,
	},
	{
		path: `/${shop}/:productUri`, // have to be the last route ou that array
		ssrActionCreators: [fetchFicheProduit, fetchPaymentMethods],
	},
	{
		path: `/${shop}`,
		ssrActionCreators: [
			fetchFirstProducts,
			fetchMerchandisings,
			fetchPaymentMethods,
			fetchDefaultTunnelSearchFilters,
		],
		ssrQueries: [getHomeHeroCommercialQuery],
	},
];
